"use client";

import { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Divider,
} from "@mui/material";
import { Add as PlusIcon } from "@mui/icons-material";
import { DrawerHeader } from "./drawer-header";
import { DrawerFooter } from "./drawer-footer";
import { SelectionList } from "./selection-list";
import { AddItemForm } from "./add-item-form";
import { Close as CloseIcon } from "@mui/icons-material";

export function GenericSelectorDrawer({
  title,
  isOpen,
  onClose,
  selectedItem,
  onSelect,
  fetchItems,
  addItem,
  editItem,
  deleteItem,
  formFields,
  fileUploadId,
  fileUploadLabel,
  emptyMessage,
  selectViewTitle,
  addViewTitle,
  editViewTitle,
  confirmLabel = "تأیید انتخاب",
  addLabel = "ذخیره",
  backLabel = "بازگشت",
  addButtonLabel = "افزودن جدید",
}) {
  const [localSelected, setLocalSelected] = useState(selectedItem || null);
  const [viewMode, setViewMode] = useState("select");
  const [editingItem, setEditingItem] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingLoading, setAddingLoading] = useState(false);
  const [editingLoading, setEditingLoading] = useState(false);
  const [newItem, setNewItem] = useState({
    name: "",
    pathimg: "",
    file: null,
  });

  const isFormValid = newItem.name.trim() !== "" && newItem.file !== null;
  const isEditFormValid = editingItem && editingItem.name && editingItem.name.trim() !== "";

  // Fetch items on mount
  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = () => {
    setLoading(true);
    fetchItems()
      .then((res) => {
        setItems(res);
      })
      .catch((err) => {
        console.error("Failed to fetch items:", err);
        setItems([]);
      })
      .finally(() => setLoading(false));
  };

  const handleSelect = (id) => {
    setLocalSelected(id);
  };

  const handleConfirm = () => {
    onSelect(localSelected);
    onClose();
  };

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      setNewItem((prev) => ({
        ...prev,
        file: e.target.files[0],
        pathimg: e.target.files[0].name,
      }));
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!isFormValid || addingLoading) return;

    setAddingLoading(true);
    try {
      await addItem(newItem);

      setNewItem({
        name: "",
        pathimg: "",
        file: null,
      });

      setViewMode("select");
      loadItems();
    } catch (err) {
      console.error("Failed to add item:", err);
    } finally {
      setAddingLoading(false);
    }
  };

  const handleEditItem = async (e) => {
    e.preventDefault();
    if (!isEditFormValid || !editItem || editingLoading) return;

    setEditingLoading(true);
    try {
      await editItem(editingItem.id, editingItem);
      setEditingItem(null);
      setViewMode("select");
      loadItems();
    } catch (err) {
      console.error("Failed to edit item:", err);
    } finally {
      setEditingLoading(false);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!deleteItem) return;
    
    try {
      await deleteItem(id);
      loadItems();
      // If deleted item was selected, clear selection
      if (localSelected === id) {
        setLocalSelected(null);
      }
    } catch (err) {
      console.error("Failed to delete item:", err);
    }
  };

  const handleStartEdit = (item) => {
    setEditingItem({
      id: item.id,
      name: item.name,
      pathimg: item.pathimg || "",
      file: null,
      // Preserve numeric IDs and user ID for API
      idsarbarg: item.idsarbarg,
      idemza: item.idemza,
      idUser: item.idUser,
      iduser: item.iduser,
    });
    setViewMode("edit");
  };

  const selectModeActions = [
    {
      label: confirmLabel,
      onClick: handleConfirm,
      disabled: !localSelected,
    },
  ];

  const addModeActions = [
    {
      label: addLabel,
      onClick: handleAddItem,
      disabled: !isFormValid || addingLoading,
      loading: addingLoading,
      variant: "contained",
    },
    {
      label: backLabel,
      onClick: () => {
        setViewMode("select");
        setNewItem({ name: "", pathimg: "", file: null });
      },
      disabled: addingLoading,
      variant: "outlined",
    },
  ];

  const editModeActions = [
    {
      label: addLabel,
      onClick: handleEditItem,
      disabled: !isEditFormValid || editingLoading,
      loading: editingLoading,
      variant: "contained",
    },
    {
      label: backLabel,
      onClick: () => {
        setViewMode("select");
        setEditingItem(null);
      },
      disabled: editingLoading,
      variant: "outlined",
    },
  ];

  return (
    <Drawer 
      anchor="bottom" 
      open={isOpen} 
      onClose={onClose}
      sx={{
        zIndex: 1000,
        "& .MuiBackdrop-root": {
          zIndex: 1099,
        },
      }}
    >
      
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
        }}
      >
        <DialogTitle sx={{ p: 0, typography: "h6", fontWeight: "medium" }}>
          {viewMode === "select" 
            ? selectViewTitle 
            : viewMode === "edit" 
            ? (editViewTitle || addViewTitle)
            : addViewTitle}
        </DialogTitle>
        <Button onClick={onClose} sx={{ minWidth: 0, p: 1 }}>
          <CloseIcon />
        </Button>
      </Box>
      <Divider />


      <DialogContent sx={{ p: 2 }}>
        {viewMode === "select" ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={<PlusIcon />}
                onClick={() => setViewMode("add")}
                sx={{ py: 1.5 }}
              >
                {addButtonLabel}
              </Button>
            </Box>

            <SelectionList
              items={items}
              selected={localSelected}
              onSelect={handleSelect}
              loading={loading}
              emptyMessage={emptyMessage}
              onEdit={editItem ? handleStartEdit : undefined}
              onDelete={deleteItem ? handleDeleteItem : undefined}
            />
          </>
        ) : viewMode === "edit" ? (
          <AddItemForm
            itemData={editingItem}
            onItemChange={setEditingItem}
            onFileChange={(e) => {
              if (e.target.files?.[0]) {
                setEditingItem((prev) => ({
                  ...prev,
                  file: e.target.files[0],
                  pathimg: e.target.files[0].name,
                }));
              }
            }}
            fields={formFields}
            fileId={fileUploadId}
            fileLabel={fileUploadLabel}
          />
        ) : (
          <AddItemForm
            itemData={newItem}
            onItemChange={setNewItem}
            onFileChange={handleFileChange}
            fields={formFields}
            fileId={fileUploadId}
            fileLabel={fileUploadLabel}
          />
        )}
      </DialogContent>

      <DrawerFooter
        actions={
          viewMode === "select" 
            ? selectModeActions 
            : viewMode === "edit"
            ? editModeActions
            : addModeActions
        }
      />
    </Drawer>
  );
}
